import React from 'react';
import { isNotEmpty } from '@techchange/pm-specs';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	FormGroup,
	Input,
	Label
} from 'reactstrap';

import { sortByWeight } from '@techchange/dnd';
import { useLanguageContext } from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages';

import { FIELD_TYPES } from '../../constants';
import { formFieldShape, formValueShape } from '../../utils';
import FormField from '../FormField';
import { CheckboxesInput, RadioInput } from '../FormInput';
import TextEditor from '../TextEditor';

const propTypes = {

	/**
	 * The form field.
	 */
	formField: formFieldShape.isRequired,

	/**
	 * The form value for the form field (if any).
	 */
	formValue: formValueShape,

	/**
	 * True, if the quiz results should include the correct answers.
	 */
	showCorrectAnswers: PropTypes.bool,

	/**
	 * True, if the quiz results should include the customized feedback.
	 */
	showFeedback: PropTypes.bool,

};

const QuizResultsQuestion = (props) => {

	const {
		formField,
		formValue,
		showCorrectAnswers,
		showFeedback,
	} = props;

	const type = formField.type;

	const intl = useIntl();

	const { chooseContentFromLanguage } = useLanguageContext();

	// Check if the user got the overall question's answer correct/incorrect
	const isQuestionCorrect = formValue ? formValue.is_answer_correct : false;

	// Keep track of ONLY the correct answers
	const correctAnswerValuesToShow = [];

	// Keep track of the options with the status of correct/incorrect answers
	const optionsToShow = sortByWeight(formField.form_field_options);

	// Check if the question supports choosing correct vs. incorrect answer choices
	const hasCorrectAndIncorrectAnswers =
		type === FIELD_TYPES.CHECKBOXES ||
		type === FIELD_TYPES.RADIO;

	// Check if we need to provide answer choice by answer choice feedback to this quiz question
	if (hasCorrectAndIncorrectAnswers) {

		const selectedOptionsByID = {};

		// Get the list of selected form field options for the form value
		const selectedFormFieldOptions = formValue?.selected_form_field_options || [];

		// Create a map of the selected options by option ID for this question
		for (let i = 0, len = selectedFormFieldOptions.length; i < len; i++) {
			selectedOptionsByID[selectedFormFieldOptions[i].id] = true;
		}

		// Loop through each of the options for the field and
		// check if the learner answered each answer choice correctly
		for (let i = 0, len = optionsToShow.length; i < len; i++) {

			const option = {
				...optionsToShow[i],
				is_user_correct: false,
				is_user_incorrect: false,
				is_user_missing: false,
			};

			// Check if the user selected the option
			const isSelected = selectedOptionsByID[option.id];

			// Check if the option is a correct answer choice
			if (option.is_correct_answer) {

				correctAnswerValuesToShow.push(option.value);

				// The user answered correctly!
				if (isSelected) {

					option.is_user_correct = true;

				// The user didn't select this correct answer choice...
				} else if (type === FIELD_TYPES.CHECKBOXES && showCorrectAnswers) {

					option.is_user_missing = true;

				}

			// Otherwise, check if the option was selected, BUT is an incorrect answer choice
			} else if (isSelected) {

				option.is_user_incorrect = true;

			}

			optionsToShow[i] = option;

		}

	}

	// Get the translated content for the form field
	const formFieldContent = chooseContentFromLanguage(formField.form_field_contents);

	const submissionFeedbackCorrect =
		formFieldContent?.submission_feedback_correct ||
		formField.submission_feedback_correct;

	const submissionFeedbackIncorrect =
		formFieldContent?.submission_feedback_incorrect ||
		formField.submission_feedback_incorrect;

	// Check if we should show the correct answer below the question
	const showCorrectAnswer =
		showCorrectAnswers &&
		!isQuestionCorrect &&
		correctAnswerValuesToShow.length > 0;

	// Check if we should show the correct feedback below the question
	const showCorrectFeedback =
		showFeedback &&
		isQuestionCorrect &&
		isNotEmpty(submissionFeedbackCorrect);

	// Check if we should show the incorrect feedback below the question
	const showIncorrectFeedback =
		showFeedback &&
		!isQuestionCorrect &&
		isNotEmpty(submissionFeedbackIncorrect);

	let inputType;

	if (type === FIELD_TYPES.CHECKBOXES) {
		inputType = 'checkbox';
	} else if (type === FIELD_TYPES.RADIO) {
		inputType = 'radio';
	}

	return (
		<FormGroup className="tc-quiz-display-results-question">
			{(!hasCorrectAndIncorrectAnswers || !formValue || !formValue.data) &&
				<FormField
					type={type}
					name={formField.id}
					value={formValue?.data || ''}
					label={formFieldContent?.title || formField.title}
					options={formField.form_field_options?.map((option) => {

						// Get the translated content for the form field option
						const formFieldOptionContent = chooseContentFromLanguage(
							option.form_field_option_contents
						);

						return {
							...option,
							label: formFieldOptionContent?.label || option.label,
						};

					})} />
			}
			{hasCorrectAndIncorrectAnswers && formValue?.data &&
				<>
					<div
						className={`tc-quiz-display-results-question-label-container
							${isQuestionCorrect ? 'is-question-correct' : 'is-question-incorrect'}`}>
						{isQuestionCorrect &&
							<span className="tc-quiz-display-results-question-answer-status-icon">
								<i
									className="fas fa-check"
									aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_ICON_LABEL_CORRECT_ANSWER)} />
							</span>
						}
						{!isQuestionCorrect &&
							<span className="tc-quiz-display-results-question-answer-status-icon">
								<i
									className="fas fa-xmark"
									aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_ICON_LABEL_INCORRECT_ANSWER)} />
							</span>
						}
						<Label>
							{formFieldContent?.title || formField.title}
						</Label>
					</div>
					{optionsToShow.map((option) => {

						// Get the translated content for the form field option
						const formFieldOptionContent = chooseContentFromLanguage(
							option.form_field_option_contents
						);

						return (
							<div
								key={option.id}
								className={`
									tc-quiz-display-results-answer-choice
									${option.is_user_correct ? 'is-answer-correct' : ''}
									${option.is_user_incorrect ? 'is-answer-incorrect' : ''}
									${option.is_user_missing ? 'is-answer-missing' : ''}
								`}>
								<FormGroup
									key={option.id}
									className="w-100"
									check>
									<Input
										type={inputType}
										name={`option-${option.id}`}
										checked={option.is_user_correct || option.is_user_incorrect}
										disabled />
									{' '}
									<Label
										className="d-flex"
										for={`option-${option.id}`}
										check>
										{
											formFieldOptionContent?.label ||
											option.label ||
											option.value
										}
										{option.is_user_correct &&
											<span className="tc-quiz-display-results-answer-choice-status-icon">
												<i
													className="fas fa-check"
													aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_ICON_LABEL_CORRECT_ANSWER)} />
											</span>
										}
										{option.is_user_incorrect &&
											<span className="tc-quiz-display-results-answer-choice-status-icon">
												<i
													className="fas fa-xmark"
													aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_ICON_LABEL_INCORRECT_ANSWER)} />
											</span>
										}
										{option.is_user_missing &&
											<span className="tc-quiz-display-results-answer-choice-status-icon">
												<i
													className="fas fa-xmark"
													aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_ICON_LABEL_MISSING_ANSWER)} />
											</span>
										}
									</Label>
								</FormGroup>
							</div>
						);

					})}
				</>
			}
			{(showCorrectAnswer || showCorrectFeedback || showIncorrectFeedback) &&
				<div
					className="tc-quiz-display-results-correct-answer-and-feedback-container"
					aria-live="polite">
					{showCorrectAnswer &&
						<div className="tc-quiz-display-results-correct-answer-container">
							<span className="tc-quiz-display-results-correct-answer-label">
								<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_LABEL_TEXT_CORRECT_ANSWER} />
							</span>
							{type === FIELD_TYPES.RADIO &&
								<RadioInput
									name={`correct-answer-for-field-${formField.id}`}
									value={correctAnswerValuesToShow[0]} />
							}
							{type === FIELD_TYPES.CHECKBOXES &&
								<CheckboxesInput
									name={`correct-answer-for-field-${formField.id}`}
									value={correctAnswerValuesToShow} />
							}
						</div>
					}
					{(showCorrectFeedback || showIncorrectFeedback) &&
						<div className="tc-quiz-display-results-feedback-container">
							<span className="tc-quiz-display-results-feedback-label">
								<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_LABEL_TEXT_FEEDBACK} />
							</span>
							{showCorrectFeedback &&
								<TextEditor
									editorDomId={`quiz-question-${formField.id}-feedback-correct`}
									content={submissionFeedbackCorrect}
									readOnly />
							}
							{showIncorrectFeedback &&
								<TextEditor
									editorDomId={`quiz-question-${formField.id}-feedback-incorrect`}
									content={submissionFeedbackIncorrect}
									readOnly />
							}
						</div>
					}
				</div>
			}
			<div className="tc-quiz-display-results-question-indicator" />
		</FormGroup>
	);

};

QuizResultsQuestion.propTypes = propTypes;

export default QuizResultsQuestion;
